import { Form } from 'formik';
import { FormikDebounce } from '../../../common/form/FormikDebounce';
import { FC } from 'react';
import * as Yup from 'yup';
import { validations } from '../../../../utils/validations';
import { Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../../../common/basic/QcsModalBox';
import { FormContainer } from '../../../common/form/FormContainer';
import { SubmitButton } from '../../../common/form/SubmitButton';
import { SectionHeader } from '../../../common/SectionHeader';
import { getFirstLastNameObj, getLangNameObj, getWorkplaceNameObj } from '../../../../utils/format';
import { Input } from '../../../common/form/Input';
import { QcsAutocomplete } from '../../../common/form/QcsAutocomplete';
import { useInvitation } from './invitationFunctions';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { InvitationState } from '@api/models/enums/InvitationState';
import { UpdateInvitedPersonRequest } from '@api/models/invitation/UpdateInvitedPersonRequest';
import { invitationPersonService } from '@api/services/services';
import { ShowError } from '../../../ShowError';
import { resources } from '@api/resources';

interface InvitedPersonsEditModalForm {
  fullName: string;
  activities: ReferenceDto[];
  workplaces: ReferenceDto[];
}

interface Props {
  data?: InvitedPersonDto;
  onClose: () => void;
}

export const InvitedPersonsEditModal: FC<Props> = ({ data, onClose }) => {
  const { t, i18n, invitation, callWithLock, reloadInvitation } = useInvitation();

  const handleSubmit = async (formData: InvitedPersonsEditModalForm) => {
    await callWithLock(async () => {
      const updateDto: UpdateInvitedPersonRequest = {
        activities: formData.activities.map((x) => x.id),
        workplaces: formData.workplaces.map((x) => x.id),
      };

      await invitationPersonService.update(data!.id!, updateDto);
      await reloadInvitation();
      onClose();
    });
  };

  return (
    <QcsModal open={!!data} onClose={onClose}>
      <QcsModalBox>
        <SectionHeader title="invitation.persons.edit" />
        <FormikDebounce<InvitedPersonsEditModalForm>
          initialValues={{
            ...data!,
            workplaces: (data?.workplaces ?? []).map((x) => ({
              id: x.id,
              name: getWorkplaceNameObj(i18n, x, invitation.workplaces?.find((w) => w.workplace?.id === x.id)?.establishment),
            })),
            fullName: getFirstLastNameObj(data),
          }}
          validationSchema={Yup.object().shape({
            activities: validations.arrayNotEmpty(t),
            workplaces: validations.arrayNotEmpty(t),
          })}
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              <Input name="fullName" label={t('invitation.persons.name')} disabled />
              <QcsAutocomplete
                name="activities"
                label={t('invitation.persons.activities')}
                required={true}
                multiple={true}
                disabled={invitation.state === InvitationState.Archived}
                options={invitation.activities?.map((x) => ({
                  id: x.activity?.id,
                  name: x.activity?.name,
                  nameEn: x.activity?.nameEn,
                }))}
                getOptionId={(option) => option.id!}
                getOptionName={(option) => getLangNameObj(i18n, option)}
                loading={false}
                loadingError={false}
              />
              <QcsAutocomplete
                name="workplaces"
                label={t('invitation.persons.workplaces')}
                required={true}
                multiple={true}
                disabled={invitation.state === InvitationState.Archived}
                options={invitation.workplaces?.map((x) => ({
                  id: x.workplace?.id,
                  name: getWorkplaceNameObj(i18n, x.workplace, x.establishment),
                }))}
                getOptionId={(option) => option.id!}
                getOptionName={(option) => getLangNameObj(i18n, option)}
                loading={false}
                loadingError={false}
              />

              <ShowError resource={resources.updatePerson} />
              <SubmitButton disabled={invitation.state === InvitationState.Archived} />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </QcsModalBox>
    </QcsModal>
  );
};
